import * as React from 'react';

import classnames from 'classnames';

import type {Row} from '../types';

import {TableCell} from './table-cell';

import styles from './table-row.module.css';

type Props<T> = {
    row: Row<T>;
    isResizable: boolean;
    isCompact: boolean;
    hasLessBottomPadding: boolean;
    isHoverable: boolean;
    isSelectable: boolean;
    hasCellBorders: boolean;
    hasRowBorders: boolean;
    showCheckbox: boolean;
    renderActionMenu?: (
        row: Row<T>,
        rowRef?: HTMLTableRowElement | null | undefined
    ) => React.ReactElement;
    onSelectRow?: (row: Row<any>) => void;
    getRef?: (ref?: HTMLDivElement | null | undefined) => void;
    onMouseOver?: (row: Row<any>) => void;
    onMouseOut?: () => void;
    extendToFitWidth?: boolean;
    isSelected?: boolean;
    expandProps?: {
        renderExpandCell: (row: Row<T>) => React.ReactElement;
        renderSubComponent: (row: Row<T>) => React.ReactElement;
        expandRowIndex?: number | null | undefined;
        clearExpandRowIndex?: () => void;
        hideExpandCell?: boolean;
        matchTableWidth?: boolean;
        expandAllRows?: boolean;
    };
};

export function TableRow<T>(props: Props<T>) {
    // we use this row ref as part of our action menu render and need to re-render the action menu
    // when the ref has been set, see https://stackoverflow.com/a/68285048 for more
    const [rowElement, setRowElement] = React.useState<HTMLTableRowElement | null | undefined>(
        null
    );
    const rowRef = React.useCallback((node?: HTMLTableRowElement | null) => {
        setRowElement(node);
    }, []);

    const {
        row,
        isResizable,
        isSelectable,
        showCheckbox,
        hasCellBorders,
        isCompact,
        hasLessBottomPadding,
        renderActionMenu,
        onSelectRow,
        onMouseOver,
        onMouseOut,
        extendToFitWidth,
        isSelected,
        expandProps,
        getRef,
    } = props;

    const {toggleRowSelected, isExpanded} = row;

    if (expandProps && expandProps.expandRowIndex === row.index && !row.isExpanded) {
        row.toggleRowExpanded();
        if (expandProps.clearExpandRowIndex) {
            expandProps.clearExpandRowIndex();
        }
    }

    if (expandProps && expandProps.expandAllRows && !row.isExpanded) {
        row.toggleRowExpanded();
    }

    const TR = isResizable ? 'div' : 'tr';
    const TD = isResizable ? 'div' : 'td';

    const styleNames = classnames(styles['table-row'], {
        [styles.hoverable]: props.isHoverable,
        [styles.bordered]: props.hasRowBorders && !isExpanded,
        [styles.selected]: isSelected,
    });

    return (
        <>
            <TR
                {...row.getRowProps()}
                ref={rowRef}
                onClick={(event) => {
                    if (isSelectable && toggleRowSelected) {
                        toggleRowSelected();
                    }

                    if (onSelectRow) {
                        onSelectRow(row);
                    }

                    if (getRef) {
                        getRef(event.currentTarget);
                    }
                }}
                onMouseEnter={onMouseOver ? () => onMouseOver(row) : undefined}
                onMouseLeave={onMouseOut ? () => onMouseOut() : undefined}
                className={styleNames}
            >
                {row.cells.map((cell, index) => {
                    return (
                        <TableCell
                            key={`${cell.column.id}-${cell.row.index}`}
                            cell={cell}
                            hasCellBorders={hasCellBorders}
                            isResizable={isResizable}
                            isCompact={isCompact}
                            hasLessBottomPadding={hasLessBottomPadding}
                            showCheckbox={index === 0 && showCheckbox}
                        />
                    );
                })}
                {extendToFitWidth ? (
                    <TD className={styles['extend-to-fit-placeholder-cell']} />
                ) : null}
                {expandProps && !expandProps.hideExpandCell ? (
                    <TD className={styles['expand-cell']}>{expandProps.renderExpandCell(row)}</TD>
                ) : null}
                {renderActionMenu ? (
                    <TD className={styles['action-button-cell']}>
                        {renderActionMenu(row, rowElement)}
                    </TD>
                ) : null}
            </TR>
            {isExpanded && expandProps
                ? wrapExpandedContent(
                      expandProps.renderSubComponent(row),
                      props.hasRowBorders,
                      expandProps.matchTableWidth,
                      row.getRowProps().style.minWidth
                  )
                : undefined}
        </>
    );
}

const wrapExpandedContent = (
    component: React.ReactNode,
    hasRowBorders: boolean,
    matchTableWidth: boolean | null | undefined,
    minWidth: number
) => {
    const styleName = hasRowBorders
        ? styles['expanded-content--with-borders']
        : styles['expanded-content'];

    return (
        <tr
            style={{width: matchTableWidth ? minWidth : '', display: matchTableWidth ? 'flex' : ''}}
        >
            <td className={styleName}>{component}</td>
        </tr>
    );
};
